import React from 'react';
import { useSignupWizard, TOTAL_STEPS } from '../../hooks/useSignupWizard';
import StepBasicInfo from './StepBasicInfo';
import StepAddress from './StepAddress';
import StepSegment from './StepSegment';
import StepBusinessSize from './StepBusinessSize';
import StepProfessionals from './StepProfessionals';
import StepEstablishmentName from './StepEstablishmentName';
import StepPassword from './StepPassword';

const SignupWizard: React.FC = () => {
    const {
        currentStep,
        formData,
        loading,
        error,
        isLoadingCep,
        updateFormData,
        nextStep,
        prevStep,
        searchCep,
        handleSubmit
    } = useSignupWizard();

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepBasicInfo
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        error={error}
                    />
                );
            case 2:
                return (
                    <StepAddress
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        onBack={prevStep}
                        searchCep={searchCep}
                        isLoadingCep={isLoadingCep}
                        error={error}
                    />
                );
            case 3:
                return (
                    <StepSegment
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        onBack={prevStep}
                        error={error}
                    />
                );
            case 4:
                return (
                    <StepBusinessSize
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        onBack={prevStep}
                        error={error}
                    />
                );
            case 5:
                return (
                    <StepProfessionals
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        onBack={prevStep}
                        error={error}
                    />
                );
            case 6:
                return (
                    <StepEstablishmentName
                        formData={formData}
                        updateFormData={updateFormData}
                        onNext={nextStep}
                        onBack={prevStep}
                        error={error}
                    />
                );
            case 7:
                return (
                    <StepPassword
                        formData={formData}
                        updateFormData={updateFormData}
                        onSubmit={handleSubmit}
                        onBack={prevStep}
                        loading={loading}
                        error={error}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen signup-wizard-bg flex items-center justify-center p-4">
            {/* Decorative Background Shapes */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-tr from-green-400 to-green-300 rounded-full opacity-60 blur-3xl"></div>
                <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-gradient-to-tl from-orange-500 to-orange-400 rounded-full opacity-70"></div>
            </div>

            {/* Main Card */}
            <div className="relative z-10 w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Header with Back Button and Progress */}
                <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        {currentStep > 1 ? (
                            <button
                                onClick={prevStep}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <span className="material-symbols-outlined text-gray-600">arrow_back</span>
                            </button>
                        ) : (
                            <div className="w-10"></div>
                        )}

                        {/* Progress Indicator */}
                        <div className="flex items-center gap-1">
                            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`
                                        h-1.5 rounded-full transition-all duration-300
                                        ${i + 1 < currentStep
                                            ? 'w-6 bg-primary'
                                            : i + 1 === currentStep
                                                ? 'w-8 bg-primary'
                                                : 'w-3 bg-gray-200'
                                        }
                                    `}
                                ></div>
                            ))}
                        </div>

                        <div className="w-10 text-right text-sm text-gray-400">
                            {currentStep}/{TOTAL_STEPS}
                        </div>
                    </div>
                </div>

                {/* Step Content */}
                <div className="p-6 sm:p-8">
                    {renderStep()}
                </div>
            </div>
        </div>
    );
};

export default SignupWizard;
